import { AutoComplete } from "antd";
import React, { useEffect, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Typography } from "../../design-system";
import { getVendors, loadVendorSuggestions } from "../../common/api/VendorClient";
import { Vendor } from "../../types/VendorTypes";
import { CompanyImage } from "../../common/components/CompanyImage";
import { NewVendorModal } from "./NewVendorModal";
import { useUserState } from "../../common/UserContext";
import { isSuperAdmin } from "../../types/UserGroup";

/**
 * Sentinel option value for the "Create vendor" row appended to the
 * autocomplete dropdown. Exported for tests.
 */
export const CREATE_VENDOR_OPTION = "__create_vendor__";

interface VendorInputArgument {
    value?: string;
    onSelect?: (vendorKey: string, vendor?: Vendor) => void;
    disabled?: boolean;
    /** Vendor keys to omit from the autocomplete dropdown (e.g. already-selected in a multi-picker). */
    excludeKeys?: string[];
    /**
     * Offer a "Create vendor" row in the dropdown. Defaults to true. The row is
     * always further gated to super admins, so non-super-admins never see it
     * regardless of this prop.
     */
    allowCreate?: boolean;
}

const renderVendorHero = (vendor: Vendor, size: number = 32) => {
    return (
        <>
            <CompanyImage logo={vendor.hero} size="xs" name={vendor.name} />
            <span style={{ paddingLeft: 10 }}>{vendor.name}</span>
        </>
    );
};

const EMPTY_VENDOR = {
    vendorKey: "",
    hero: "",
    name: "",
    url: "",
    description: "",
} as Vendor;

export const VendorInput: React.FC<VendorInputArgument> = ({
    value,
    onSelect,
    disabled,
    excludeKeys,
    allowCreate = true,
}) => {
    const { me } = useUserState();
    const canCreate = allowCreate && isSuperAdmin(me.roles || []);
    const [searchValue, setSearchValue] = useState("");
    const [vendorOptions, setVendorOptions] = useState([] as Vendor[]);
    const [showSelector, setShowSelector] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(EMPTY_VENDOR);
    const [showNewVendorModal, setShowNewVendorModal] = useState(false);

    // Keep the displayed vendor in sync with the `value` prop.
    useEffect(() => {
        if (!value) {
            setSelectedVendor(EMPTY_VENDOR);
            return;
        }
        const ac = new AbortController();
        getVendors([value], { signal: ac.signal })
            .then((vendors) => {
                if (vendors.length > 0) setSelectedVendor(vendors[0]);
            })
            .catch(() => {});
        return () => ac.abort();
    }, [value]);

    useEffect(() => {
        const ac = new AbortController();
        loadVendorSuggestions(searchValue, { signal: ac.signal })
            .then((vendors) => {
                setVendorOptions(vendors);
            })
            .catch(() => {});
        return () => ac.abort();
    }, [searchValue]);

    const pickVendor = (vendor: Vendor) => {
        setSelectedVendor(vendor);
        setShowNewVendorModal(false);
        setShowSelector(false);
        setSearchValue("");
        onSelect?.(vendor.vendorKey, vendor);
    };

    const newVendorModal = showNewVendorModal ? (
        <NewVendorModal
            initialName={searchValue.trim()}
            onCreate={pickVendor}
            onUseExisting={pickVendor}
            onCancel={() => setShowNewVendorModal(false)}
        />
    ) : (
        <></>
    );

    const excluded = new Set(excludeKeys ?? []);
    const trimmedSearch = searchValue.trim();
    const vendorOpts = vendorOptions
        .filter((v) => !excluded.has(v.vendorKey))
        .map((v) => ({ value: v.vendorKey, label: renderVendorHero(v) }));
    // The create row goes last so Enter still picks the best match when one
    // exists; with no matches it is the only row, so Enter opens the modal.
    // That relies on `defaultActiveFirstOption` below: rc-select turns it off
    // for combobox mode, which left Enter doing nothing at all.
    const options =
        canCreate && trimmedSearch
            ? [
                  ...vendorOpts,
                  {
                      value: CREATE_VENDOR_OPTION,
                      label: (
                          <span data-testid="vendor-create-option">
                              <PlusOutlined /> Create vendor "{trimmedSearch}"
                          </span>
                      ),
                  },
              ]
            : vendorOpts;

    const input = (
        <AutoComplete
            size={"small"}
            value={searchValue}
            options={options}
            defaultActiveFirstOption
            style={{ minWidth: 100 }}
            notFoundContent={
                canCreate && !trimmedSearch
                    ? "Type a vendor name to search or create"
                    : "No vendors found"
            }
            onSelect={(selected: any) => {
                if (selected === CREATE_VENDOR_OPTION) {
                    setShowNewVendorModal(true);
                    return;
                }
                const vendor = vendorOptions.find((v) => v.vendorKey === selected);
                if (vendor) {
                    setSelectedVendor(vendor);
                }
                onSelect?.(selected as string, vendor);
                setShowSelector(false);
            }}
            onSearch={(text) => {
                setSearchValue(text);
            }}
            onKeyDown={(e) => {
                if (e.key === "Escape") {
                    setShowSelector(false);
                    setSearchValue("");
                }
            }}
            disabled={disabled}
            placeholder={canCreate ? "Search or create vendor" : "Vendor Name"}
        ></AutoComplete>
    );

    const vendorLine = selectedVendor.vendorKey ? (
        <>
            <CompanyImage logo={selectedVendor.hero} size="xs" name={selectedVendor.name} />
            <span style={{ paddingLeft: 10 }}>{selectedVendor.name}</span>
        </>
    ) : (
        <Typography.Text type="secondary">
            {canCreate ? "Select or create vendor…" : "Select vendor…"}
        </Typography.Text>
    );
    const editLink = (
        <a
            href="http://example.com"
            onClick={(e) => {
                setShowSelector(true);
                e.preventDefault();
            }}
        >
            {vendorLine}
        </a>
    );
    const vendorHero = <div>{disabled ? vendorLine : editLink}</div>;
    return (
        <>
            {newVendorModal}
            {showSelector ? input : vendorHero}
        </>
    );
};
